import type { ShareDialogConfiguration, SnapshotDialogConfiguration } from '../utils/dialogUtils';
import type {
    DialogManagerState,
    DialogOptions,
    IframeBarStyle,
    InputDialogOptions,
    ColorDialogConfiguration,
    ErrorDialogConfiguration,
    InputDialogConfig,
    ModalInputDialogConfig,
} from '@egr/xbox/egr-gui-dialogs/Types';
import type { LoadingDialogConfiguration, QrDialogConfiguration } from '@egr/xbox/egr-gui-dialogs/utils/DialogUtils';

import bind from 'bind-decorator';
import i18next from 'i18next';

import { languageStore } from './LanguageStore';
import ToastStore from './ToastStore';

import { openBrowser } from '@egr/xbox/app-api/AppAction';
import { isReactNativeApp } from '@egr/xbox/app-api/AppDetection';
import { appInfo } from '@egr/xbox/app-api/AppInfo';
import { shareText } from '@egr/xbox/app-api/Share';
import { ColorDialogColors, DATA_PRIVACY_TXT, DialogType, LEGAL_NOTICE_URL_TXT, TERMS_OF_USE_TXT } from '@egr/xbox/egr-gui-dialogs/Constants';
import { messageManager } from '@egr/xbox/messagebar/MessageManager';
import { copyToClipBoard } from '@egr/xbox/utils/Clipboard';
import { downloadUrl } from '@egr/xbox/utils/Download';
import { doNothing } from '@egr/xbox/utils/Helper';
import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';
import { getIOSVersion, ios, inPlanner } from '@egr/xbox/utils/UserAgent';
import { addBreadcrumb } from '@egr/xbox/utils/errors/Breadcrumb';

import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

const closedDialogs = {
    simpleDialog: null,
    dialog: null,
    loadingDialog: null,
    colorDialog: null,
    errorDialog: null,
    qrDialog: null,
    shareDialog: null,
    embedDialog: null,
    snapShotDialog: null,
};

interface ImpressDialogState extends DialogManagerState {
    shareDialog: ShareDialogConfiguration | null;
    embedDialog: ShareDialogConfiguration | null;
    snapShotDialog: SnapshotDialogConfiguration | null,
}

class DialogManager extends SyncExternalStore<ImpressDialogState> {
    protected static fullscreenCondition(): boolean {
        return window.innerWidth <= 568 || window.innerHeight <= 568;
    }

    constructor() {
        super({
            iframeUri: undefined,
            iframeTitle: '',
            iframeBarStyle: 'default',

            showDialogFullscreen: DialogManager.fullscreenCondition(),

            youtubeLinkId: undefined,
            youtubeTitle: undefined,
            youtubeDialogOpen: false,
            youtubeVideoStatus: undefined,
            youtubeWarning: true,

            simpleDialog: null,
            dialog: null,
            loadingDialog: null,
            colorDialog: null,
            errorDialog: null,
            qrDialog: null,
            impressShare: null,
            shareDialog: null,
            embedDialog: null,
            snapShotDialog: null,
        });
        this.closeDialog = this.closeDialog.bind(this);
        this.copyTextToClipboard = this.copyTextToClipboard.bind(this);
        this.onConnectionError = this.onConnectionError.bind(this);
        this.onResize = this.onResize.bind(this);
        this.openLegalNotice = this.openLegalNotice.bind(this);
        this.openTermsOfUse = this.openTermsOfUse.bind(this);
        this.showColorDialog = this.showColorDialog.bind(this);
        this.showInputDialog = this.showInputDialog.bind(this);
        this.showLoadingDialog = this.showLoadingDialog.bind(this);
        this.showModalDialog = this.showModalDialog.bind(this);
        this.triggerErrorDialog = this.triggerErrorDialog.bind(this);
        this.updateDialog = this.updateDialog.bind(this);
        this.openDataPrivacy = this.openDataPrivacy.bind(this);
    }

    public initialize(): void {
        window.addEventListener('resize', this.onResize);
    }

    public showSimpleDialog(options: DialogOptions, sentryBreadcrumb: string = 'no title set'): void {
        addBreadcrumb({
            category: 'LatestDialog',
            message: sentryBreadcrumb,
        });

        this.updateDialog(DialogType.SIMPLE, options);
    }

    public showDialog(dialog: InputDialogOptions, sentryBreadcrumb: string = 'no title set'): void {
        addBreadcrumb({
            category: 'LatestDialog',
            message: sentryBreadcrumb,
        });

        this.updateDialog(DialogType.DEFAULT, dialog);
    }

    public triggerErrorDialog(errorDialog: ErrorDialogConfiguration | null): void {
        this.updateDialog(DialogType.ERROR, errorDialog);
    }
    public showShareDialog(options: ShareDialogConfiguration, sentryBreadcrumb: string = 'no title set'): void {
        addBreadcrumb({
            category: 'LatestDialog',
            message: sentryBreadcrumb,
        });

        this.updateDialog(DialogType.SHARE, options);
    }
    public showEmbedDialog(options: ShareDialogConfiguration, sentryBreadcrumb: string = 'no title set'): void {
        addBreadcrumb({
            category: 'LatestDialog',
            message: sentryBreadcrumb,
        });

        this.updateDialog(DialogType.EMBED, options);
    }

    public showSnapshotDialog(options: SnapshotDialogConfiguration, sentryBreadcrumb: string = 'no title set'): void {
        addBreadcrumb({
            category: 'LatestDialog',
            message: sentryBreadcrumb,
        });

        this.updateDialog(DialogType.SNAPSHOT, options);
    }


    public closeDialog(whichDialog?: DialogType): void {
        switch (whichDialog) {
            case DialogType.SIMPLE:
                return this.updateState({ simpleDialog: null });
            case DialogType.COLOR:
                return this.updateState({ colorDialog: null });
            case DialogType.DEFAULT:
                return this.updateState({ dialog: null });
            case DialogType.ERROR:
                return this.updateState({ errorDialog: null });
            case DialogType.LOADING:
                return this.updateState({ loadingDialog: null });
            case DialogType.QR:
                return this.updateState({ qrDialog: null });
            case DialogType.SHARE:
                return this.updateState({ shareDialog: null });
            case DialogType.EMBED:
                return this.updateState({ embedDialog: null });
            case DialogType.EMBED:
                return this.updateState({ snapShotDialog: null });

            default:
                return this.updateState(closedDialogs);
        }
    }

    public showLoadingDialog(label: string, closeCallback?: () => void, value?: number | string): void {
        if (value && typeof value === 'number' && isNaN(value)) {
            value = undefined;
        }
        this.updateDialog(DialogType.LOADING, { label: label, closeCallback, value: value });
    }

    @bind
    public async showCancelDialog<T>(promise: Promise<T>, label: string): Promise<T | null> {
        let canceled: boolean = false;
        const cancelCallback: () => void = () => {
            this.closeDialog(DialogType.LOADING);
            canceled = true;
        };

        this.showLoadingDialog(label, cancelCallback);

        const result: T = await promise;

        if (canceled) {
            return null;
        }

        this.closeDialog(DialogType.LOADING);

        return result;
    }

    public async showModalDialog(
        config: ModalInputDialogConfig,
        sentryBreadcrumb: string = 'no title set',
        okLabel: string = i18next.t('Ok'),
        cancelLabel: string = i18next.t('Cancel'),
        onBackgroundClick?: (resolve: (value: boolean) => void, reject: (error: Error) => void) => void
    ): Promise<boolean | undefined> {
        return new Promise<boolean | undefined>((resolve, reject) => {
            this.showDialog(
                {
                    ...config,
                    buttons: [
                        {
                            label: cancelLabel,
                            key: 'cancel-export',
                            callback: (): void => {
                                resolve(false);
                            },
                        },
                        {
                            label: okLabel,
                            key: 'input-submit',
                            callback: (): void => {
                                resolve(true);
                            },
                            mainButton: true,
                        },
                    ],
                    onClose: (): void => {
                        this.closeDialog();
                        resolve(undefined);
                    },
                    onBackdropClick: (): void => {
                        if (onBackgroundClick != null) {
                            onBackgroundClick(resolve, reject);
                        } else {
                            this.closeDialog();
                            resolve(undefined);
                        }
                    },
                },
                `${sentryBreadcrumb} (modal)`
            );
        });
    }

    public async showInputDialog<ResultType = string | number>(
        dialogConfig: Partial<Omit<InputDialogConfig, 'input'>>,
        inputConfig?: InputDialogConfig['input'],
        onClose?: (resolve: (value?: ResultType) => void, reject: (error: Error) => void) => void,
        okText: string = i18next.t('Ok'),
        cancelText: string = i18next.t('Cancel'),
        onCancel: (resolve: (value?: ResultType) => void, reject: (error: Error) => void) => void = (resolve: (value?: ResultType) => void) => {
            resolve();
        }
    ): Promise<ResultType | undefined> {
        return new Promise<ResultType | undefined>((resolve: (value?: ResultType) => void, reject: (error: Error) => void): void => {
            const cancel: VoidFunction = () => {
                this.closeDialog();
                onCancel(resolve, reject);
            };

            const close: VoidFunction = (): void => {
                if (onClose != null) {
                    onClose(resolve, reject);
                } else {
                    cancel();
                }
            };

            const ok: VoidFunction = (value?: ResultType): void => {
                this.closeDialog();
                resolve(value);
            };

            this.updateDialog(DialogType.DEFAULT, {
                ...dialogConfig,
                input: {
                    autoFocus: true,
                    ...inputConfig,
                },
                buttons: dialogConfig.buttons ?? [
                    { label: cancelText, key: 'input-cancel', callback: cancel },
                    { label: okText, key: 'input-submit', checkInput: true, callback: ok, mainButton: true },
                ],
                onClose: close,
                onBackdropClick: close,
                placeholder: inputConfig?.placeholder,
            });
        });
    }

    public async showColorDialog(
        color: string | undefined,
        colors: Array<string | undefined> = ColorDialogColors,
        extended: boolean = false
    ): Promise<string | undefined> {
        return new Promise<string | undefined>((resolve) => {
            this.updateDialog(DialogType.COLOR, {
                color,
                colors,
                resolve: (newColor: string | undefined) => {
                    this.closeDialog(DialogType.COLOR);
                    resolve(newColor);
                },
                extended,
            });
        });
    }

    public showIframe = (iframeUri: string, iframeTitle: string = '', iframeBarStyle: IframeBarStyle = 'default'): void => {
        this.updateState({ iframeUri, iframeTitle, iframeBarStyle });
    };

    public hideIframe = (): void => {
        this.updateState({ iframeUri: undefined });
    };

    /**
     * Workaround for iOS 12.x issue with download and popup blocker in Safari
     * @param href
     * @param download
     * @param target
     */
    public downloadHelper(href: string, download: string, target: '_blank' | '_self' | '_parent' | '_top' = '_blank', anyIOS: boolean = false): void {
        if (ios && (anyIOS || getIOSVersion() < 13)) {
            const result: string = href.substring(href.lastIndexOf('/') + 1);
            const filename = isNotNullOrEmpty(result) ? result : download;
            return this.showDialog(
                {
                    title: i18next.t('Do you want to download "{{filename}}"?', { filename }),
                    buttons: [
                        {
                            label: i18next.t('Download'),
                            key: 'download',
                            type: 'link',
                            href,
                            download,
                        },
                        {
                            label: i18next.t('Cancel'),
                            key: 'close',
                        },
                    ],
                },
                'DownloadHelper'
            );
        }
        return void downloadUrl(href, download, target);
    }

    @bind
    public async openYoutube(youtubeLinkId: string, name?: string): Promise<void> {
        if (inPlanner) {
            const a: HTMLAnchorElement = document.createElement('a');
            a.href = `https://www.youtube.com/watch?v=${youtubeLinkId}`;
            a.rel = 'noopener noreferrer';
            a.target = '_blank';
            return a.click();
        }

        if (ios && isReactNativeApp && !this.getSnapshot().youtubeWarning) {
            return openBrowser(`https://www.youtube-nocookie.com/embed/${youtubeLinkId}?rel=0&modestbranding=1&autoplay=1`);
        }

        this.updateState({
            youtubeLinkId: youtubeLinkId,
            youtubeTitle: name ? name : '',
            youtubeDialogOpen: true,
        });
    }

    @bind
    public closeYoutube(): void {
        this.updateState({
            youtubeLinkId: undefined,
            youtubeTitle: undefined,
            youtubeDialogOpen: false,
            youtubeVideoStatus: 'ended',
        });
    }

    public onConnectionError(retryCallback: VoidFunction): void {
        this.showDialog(
            {
                title: i18next.t('Connection interrupted'),
                buttons: [
                    {
                        label: i18next.t('Connect'),
                        key: 'reconnect',
                        callback: (): void => {
                            retryCallback();
                            this.closeDialog();
                        },
                    },
                ],
                onClose: doNothing,
            },
            'NetworkError'
        );
    }
    public updateDialog(type: DialogType.SHARE, config: ShareDialogConfiguration | null);
    public updateDialog(type: DialogType.EMBED, config: ShareDialogConfiguration | null);
    public updateDialog(type: DialogType.SNAPSHOT, config: SnapshotDialogConfiguration | null);
    public updateDialog(type: DialogType.SIMPLE, config: DialogOptions | null);
    public updateDialog(type: DialogType.DEFAULT, config: InputDialogOptions | null);
    public updateDialog(type: DialogType.LOADING, config: LoadingDialogConfiguration | null);
    public updateDialog(type: DialogType.COLOR, config: ColorDialogConfiguration | null);
    public updateDialog(type: DialogType.ERROR, config: ErrorDialogConfiguration | null);
    public updateDialog(type: DialogType.QR, config: QrDialogConfiguration | null);
    public updateDialog(type: DialogType.ALL, config: null);
    public updateDialog(type: DialogType, config: null): void {
        switch (type) {
            case DialogType.SIMPLE:
                return this.updateState({ ...closedDialogs, simpleDialog: config });
            case DialogType.COLOR:
                return this.updateState({ ...closedDialogs, colorDialog: config });
            case DialogType.DEFAULT:
                return this.updateState({ ...closedDialogs, dialog: config });
            case DialogType.ERROR:
                return this.updateState({ ...closedDialogs, errorDialog: config });
            case DialogType.LOADING:
                return this.updateState({ ...closedDialogs, loadingDialog: config });
            case DialogType.QR:
                return this.updateState({ ...closedDialogs, qrDialog: config });
            case DialogType.SHARE:
                return this.updateState({ ...closedDialogs, shareDialog: config });
            case DialogType.EMBED:
                return this.updateState({ ...closedDialogs, embedDialog: config });
            case DialogType.SNAPSHOT:
                return this.updateState({ ...closedDialogs, snapShotDialog: config });
            default:
                return this.updateState(closedDialogs);
        }
    }

    public async copyTextToClipboard(text: string): Promise<void> {
        if (appInfo != null) {
            await shareText(text);
        } else if (!(await copyToClipBoard(text))) {
            return messageManager.showMessage(i18next.t('Copy failed'));
        } else {
            if (text.startsWith('http')) {
                ToastStore.open(i18next.t('Link copied to clipboard'));
            } else {
                ToastStore.open(i18next.t('Text copied to clipboard'));
            }
        }
    }

    /** Impressum */
    public async openLegalNotice(): Promise<void> {
        const content = await fetch(LEGAL_NOTICE_URL_TXT.replace(/\<LANGUAGE\>/, languageStore.snapshot.language));

        this.showSimpleDialog(
            {
                title: i18next.t('Legal notice'),
                subTitle: [
                    <div
                        key="text"
                        dangerouslySetInnerHTML={{ __html: await content.text() }}
                    />,
                ],
                buttons: [],
                fullscreen: true,
                onClose: this.closeDialog,
            },
            'legal_url'
        );
    }

    public async openTermsOfUse(): Promise<void> {
        const content = await fetch(TERMS_OF_USE_TXT.replace(/\<LANGUAGE\>/, languageStore.snapshot.language));

        this.showSimpleDialog(
            {
                title: i18next.t('Terms of use'),
                subTitle: [
                    <div
                        key="text"
                        dangerouslySetInnerHTML={{ __html: await content.text() }}
                    />,
                ],
                buttons: [],
                fullscreen: true,
                onClose: this.closeDialog,
            },
            'terms_of_use_url'
        );
    }

    public async openDataPrivacy(): Promise<void> {
        const content = await fetch(DATA_PRIVACY_TXT.replace(/\<LANGUAGE\>/, languageStore.snapshot.language));

        this.showSimpleDialog(
            {
                title: i18next.t('Data privacy'),
                subTitle: [
                    <div
                        key="text"
                        dangerouslySetInnerHTML={{ __html: await content.text() }}
                    />,
                ],
                buttons: [],
                fullscreen: true,
                onClose: this.closeDialog,
            },
            'data_privacy_url'
        );
    }

    protected onResize(): void {
        this.updateState({ showDialogFullscreen: DialogManager.fullscreenCondition() });
    }
}

export const dialogManager = new DialogManager();
